import axios from "axios";
import { jwtDecode } from "jwt-decode";

// Create an Axios instance
const isLocalhost = window.location.hostname === "localhost";
export const api = axios.create({
  baseURL: isLocalhost
    ? "http://localhost:8080/api/v1" // Staging (local dev)
    : "https://codequeue.in/api/v1", // Production
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to attach token dynamically
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.exp * 1000 < Date.now()) {
          logoutUser(); // Token is expired, log out
          return Promise.reject({ message: "Token expired" });
        }
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        logoutUser(); // If decoding fails, log out
        return Promise.reject({ message: "Invalid token" });
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Logout function
const logoutUser = () => {
  localStorage.removeItem("token");
  window.location.href = "/login";
};

//------------------------------------------Authentication and authorization------------------------------------------

//send email otp
export const sendEmailOtp = async (email) => {
  const response = await api.post("/send-email-otp", { email });
  return response.data;
};
//verify email otp
export const verifyEmailOtp = async (email, mailOtp) => {
  const response = await api.post("/verify-email-otp", { email, mailOtp });
  return response.data;
};

//send number otp
export const sendNumberOtp = async (phone) => {
  const response = await api.post("/send-otp", { phone });
  return response.data;
};

//verify number otp
export const verifyNumberOtp = async (phone, otp) => {
  const response = await api.post("/verify-otp", { phone, otp });
  return response.data;
};

//check if profile is user or mentor
export const getProfileRole = async (userId) => {
  const response = await api.post("/profiles", { userId });
  return response.data;
};

//signup as an user
export const userSignup = async (name, email, phone, password, dob, gender) => {
  const response = await api.post("/signup", { name, email, phone, password, dob, gender });
  return response.data;
};

//login as an user
export const userLogin = async (userId, password) => {
  const response = await api.post("/login", { userId, password });
  return response.data;
};

//signup as a mentor
export const mentorSignup = async (
  name,
  email,
  phone,
  currentCompany,
  graduationPassingYear,
  password,
  isAcceptedPolicy,
  dob, 
  gender
) => {
  const response = await api.post("/mentor-signup", {
    name,
    email,
    phone,
    currentCompany,
    graduationPassingYear,
    password,
    isAcceptedPolicy,
    dob, 
    gender
  });
  return response.data;
};

//login as an mentor
export const mentorLogin = async (userId, password) => {
  const response = await api.post("/mentor-login", { userId, password });
  return response.data;
};

//forget password
export const updatePassword = async (email, password, role) => {
  const response = await api.post("/new-password", { email, password, role });
  return response.data;
};

// --------------------------------------Dashboard api------------------------------------------

//get one mentor profile details
export const getOneMentorDetails = async (email) => {
  const response = await api.post("/get-mentor-profile-details", { email });
  return response.data;
};

//update mentor profile details
export const updateMentorProfileDetails = async (
  email,
  profileImage,
  name,
  about,
  heading,
  college,
  selectedDomain,
  selectedCountry,
  selectedState,
  selectedLanguage,
  currentCompany,
  selectedDesignation,
  selectedSkills,
  selectedExperience,
  selectedDegree,
  passingYear
) => {
  const response = await api.post("/mentor-profile-details", {
    email,
    profileImage,
    name,
    about,
    heading,
    college,
    selectedDomain,
    selectedCountry,
    selectedState,
    selectedLanguage,
    currentCompany,
    selectedDesignation,
    selectedSkills,
    selectedExperience,
    selectedDegree,
    passingYear,
  });
  return response.data;
};

//get mentor sessions
export const getMentorSessions = async (email) => {
  const response = await api.post("/new-session", { email });
  return response.data;
};

//add mentor sessions
export const addMentorSessions = async (email, title, type, duration, amount) => {
  const response = await api.post("/new-session", {
    email,
    title,
    type,
    duration,
    amount,
  });
  return response.data;
};

//delete mentor sessions
export const deleteMentorSessions = async (email, index) => {
  const response = await api.post("/new-session", { email, index });
  return response.data;
};

//update mentor sessions
export const updateMentorSessions = async (
  email,
  title,
  type,
  duration,
  amount,
  index
) => {
  const response = await api.post("/new-session", {
    email,
    title,
    type,
    duration,
    amount,
    index,
  });
  return response.data;
};

//get available slots details
export const getAvailableSlots = async (email) => {
  const response = await api.post("/available-slots", { email });
  return response.data;
};

//update available slots details
export const updateAvailableSlots = async (email, slots) => {
  const response = await api.post("/available-slots", { email, slots });
  return response.data;
};

// get mentor list
export const getMentorList = async ({
  page = 1,
  limit = 10,
  domain,
  company,
  skills,
  language,
  keyword,
}) => {
  const params = {
    page,
    limit,
    ...(domain && { domain }),
    ...(company && { company }),
    ...(skills && { skills: skills.join(",") }),
    ...(language && { language: language.join(",") }),
    ...(keyword && { keyword }),
  };
  const response = await api.get("/mentor-list", { params });
  return response.data;
};

// get mentor available session
export const getMentorSessionForUser = async (id) => {
  const response = await api.post("/mentor-sessions", { id });
  return response.data;
};

// get mentor available slots by user
export const getMentorSlotsForUser = async (id) => {
  const response = await api.post("/slots-defined-by-mentor", { id });
  return response.data;
};

// generate referral code
export const generateReferralCode = async (email) => {
  const response = await api.post("/generate-referral-code", { email });
  return response.data;
};

// get referral code
export const getReferralsData = async (email) => {
  const response = await api.post("/get-referral-code", { email });
  return response.data;
};

//create referrals
export const createReferrals = async (referralsData) => {
  const response = await api.post("/create-referrals", referralsData);
  return response.data;
};

// submit candidates details for jobs
export const submitJobApplications = async (formData) => {
  const response = await api.post("/apply", formData, {
    headers: {
      "Content-Type": "multipart/form-data", // Set the content type for file upload
    },
  });
  return response.data;
};

// check if user and mentor are same
export const isUserAndMentorSame = async (id, email) => {
  const response = await api.post("/is-email-matched", { id, email });
  return response.data;
};

// get booking details
export const getBookingDetails = async (email, role) => {
  const response = await api.post("/get-booking-details", { email, role });
  return response.data;
};

// get amount withdrawal history
export const getBookingAmount = async (email) => {
  const response = await api.post("/get-bookings-amount", { email });
  return response.data;
};

// add bank account details
export const addBankAccountDetails = async (bankDetails) => {
  const response = await api.post("/add-bank-account-details", bankDetails);
  return response.data;
};

// get bank account details
export const getBankAccountDetails = async (email) => {
  const response = await api.post("/get-bank-account-details", { email });
  return response.data;
};

// get meeting id
export const getMeetingId = async (id) => {
  const response = await api.post("/get-meeting-id", { id });
  return response.data;
};

// get booking summary
export const getBookingSummary = async (email) => {
  const response = await api.post("/get-booking-summary", { email });
  return response.data;
};

//get transactions history
export const getTransactionsHistory = async (email) => {
  const response = await api.post("/get-transactions-history", { email });
  return response.data;
};

// update pending bookings amount
export const updatePendingBookingAmount = async (id) => {
  const response = await api.post("/update-pending-bookings-amount", { id });
  return response.data;
};

// requestfeature
export const requestFeature = async (formData) => {
  const response = await api.post("/feature-request", formData, {
    headers: {
      "Content-Type": "multipart/form-data", // Set the content type for file upload
    },
  });
  return response.data;
};

// update phone number
export const updatePhoneNumber = async (email, phone, role) => {
  const response = await api.post("/update-phone-number", {
    email,
    phone,
    role,
  });
  return response.data;
};

// get account data
export const getAccountData = async (email, role) => {
  const response = await api.post("/get-account-data", { email, role });
  return response.data;
};

//request payment withdraw
export const requestPaymentWithdraw = async (name) => {
  const response = await api.post("/service-amount-withdraw-request", {
    name,
  });
  return response.data;
};

// get getWithdrawal History
export const getWithdrawalHistory = async (email) => {
  const response = await api.post("/get-withdrawal-history", { email });
  return response.data;
};

// get user and mentor data for user reveiw
export const getMentorDataForUserReview = async (id) => {
  const response = await api.post("/get-data-for-user-review", { id });
  return response.data;
};

// review
export const review = async (reviewData) => {
  const response = await api.post("/review", reviewData);
  return response.data;
};

// cancel booking
export const cancelBooking = async (cancellationData) => {
  const response = await api.post("/cancel-booking", cancellationData);
  return response.data;
};
// Reschedule booking
export const rescheduleBooking = async (formData) => {
  const response = await api.post("/reschedule-booking", formData);
  return response.data;
};
// get review and ratings
export const fetchReviewAndRatings = async (id, limit) => {
  const response = await api.post("/get-review-ratings", { id, limit });
  return response.data;
};
// Mentor profile verification Request
export const verifyMentor = async ({ email }) => {
  const response = await api.post("/mentor-profile-request", { email });
  return response.data;
};

// send priority dm
export const sendPriorityDm = async (formData) => {
  const response = await api.post("/priority-dm", formData);
  return response.data;
};

// get mentor profile completion progress status
export const getMentorProfileCompletionStatus = async (email) => {
  const response = await api.post("/get-mentor-profile-completion-status", {
    email,
  });
  return response.data;
};
// Function to get all priority DMs
export const getPriorityDms = async (mentorEmail) => {
  try {
    const response = await api.get(`/priority-dms?mentor_email=${mentorEmail}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching priority DMs:", error);
    throw error;
  }
};
// Reject Priority DM API
export const rejectPriorityDm = async (_id) => {
  try {
    const response = await api.post("/reject-priority-dm", { _id });
    return response.data;
  } catch (error) {
    console.error("Error rejecting priority DM:", error);
    throw error;
  }
};

// Update User Response API
export const updateUserResponse = async (_id, response) => {
  try {
    const apiResponse = await api.post("/update-priority-dm", {
      _id,
      response,
    });
    return apiResponse.data;
  } catch (error) {
    console.error("Error updating priority DM:", error);
    throw error;
  }
};

// get all job openings
export const getJobOpenings = async () => {
  const response = await api.get("/jobs");
  return response.data;
};

// get a job by id
export const getJobOpeningsById = async (jobId) => {
  const response = await api.post(`/jobs/:${jobId}`, { jobId });
  return response.data;
};

// get mentor's details by their unique id
export const getMentorsById = async (id) => {
  const response = await api.get(`/get-mentor-by-id/${id}`); // Pass `id` in the URL
  return response.data;
};

// update available slot to remove the slot
export const updateSlotCount = async (formData) => {
  const response = await api.post("/update-slot-count", formData);
  return response.data;
};

// contact
export const getContact = async (formData) => {
  const response = await api.post("/send-mail", formData);
  return response.data;
};

// get meeting id and server secrets
export const getAppIdNSecrets = async () => {
  const response = await api.get("/get-app-id-server-secrets");
  return response.data;
};

// site key for recaptcha
export const getRecaptchaSiteKey = async () => {
  const response = await api.get("/recaptcha-site-key");
  return response.data;
};


// get UPI id to pay referral amout 
export const upiDetails = async (formData) => {
  const response = await api.post("/upi-data", formData);
  return response.data;
};

// get UPI data
export const getUpiDetails = async () => {
  const response = await api.get("/get-upi-data");
  return response.data;
};

// referral amount withdraw request
export const referralAmoutWithdraw = async (name) => {
  const response = await api.post("/referral-amount-withdraw", {name});
  return response.data;
};

// get referral requested amout for withdrawal
export const geRreferralAmoutWithdraw = async () => {
  const response = await api.get("/get-referral-requested-amount");
  return response.data;
};

// account deletion 
export const accountDeletion = async (otp) => {
  const response = await api.post("/account-delete",{otp});
  return response.data;
};

//get top mentors 
export const getTopMentors = async (limit = 5) => {
  const response = await api.get(`/top-mentors?limit=${limit}`);
  return response.data.data;
};

//api to run the code
export const runCode = async (data) => {
  const response = await api.post('/run-code', data);
  return response.data;
};

export const getReferralRequests = async (mentorEmail) => {
  try {
    const response = await api.get(`/referral-requests?mentor_email=${mentorEmail}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateReferralResponse = async (id, response) => {
  try {
    const result = await api.post("/update-referral-request", {
      _id: id,
      response: response,
    });
    return result.data;
  } catch (error) {
    throw error;
  }
};

export const rejectReferralRequest = async (id) => {
  try {
    const response = await api.post("/reject-referral-request", {
      _id: id,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};





// --------------------------------API FOR EVENTS------------------------------------
// get the candidate details
export const bhopalFirstEventData = async () => {
  const response = await api.get("/bhopal-first-event");
  return response.data;
};

// register for the event
export const registerForEvent = async (formData) => {
  const response = await api.post("/bhopal-first-event", formData);
  return response.data;
};

// register for the event
export const contestResult = async () => {
  const response = await api.get("/contest-result");
  return response.data;
};

// generate the otp for contest authentication 
export const generateContestOtp = async(passKey)=>{
  const response = await api.post("/generate-contest-otp",{passKey});
  return response.data;
}

// Attendance marked 
export const markAttan = async(email)=>{
  const response = await api.post("/marked-attendance",{email});
  return response.data;
}